import { useCallback, useEffect, useState } from 'react';
import Carousel from '@alltrails/denali/components/Carousel';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import PaddingAwarePopup from '../PaddingAwarePopup';
import PopupCarouselControls from '../PopupCarouselControls';
import { UserCustomRoute } from '../../types/CustomRoute';
import { pinPopupOffset, clusterPopupOffset } from '../../utils/svgDefinitions/svgMarkerComponents';
import MapCard from './UserCustomRouteCard';
import styles from './styles/ActiveUserCustomRoutePopup.module.scss';

type ActiveUserCustomRoutePopupProps = {
  latitude: number;
  longitude: number;
  routes: UserCustomRoute[];
  initialRouteIndex?: number;
  onRouteChange: (route: UserCustomRoute, index: number) => void;
  onClick?: (id: number, slug: string) => void;
  onEnter?: (e: Event) => void;
  onLeave?: () => void;
  isFavorite: (type: string, id: number) => boolean;
  onFavoriteClick: (currentResult: UserCustomRoute) => void;
};

const ActiveUserCustomRoutePopup = ({
  latitude,
  longitude,
  routes,
  initialRouteIndex = 0,
  onRouteChange,
  onClick,
  onEnter,
  onLeave,
  isFavorite,
  onFavoriteClick
}: ActiveUserCustomRoutePopupProps) => {
  const [index, setIndex] = useState(initialRouteIndex);
  const isMobile = useIsMobileSizedScreen();

  useEffect(() => {
    setIndex(initialRouteIndex);
  }, [initialRouteIndex]);

  const onIndexChange = useCallback(
    (newIndex: number) => {
      setIndex(newIndex);
      onRouteChange(routes[newIndex], newIndex);
    },
    [routes, onRouteChange]
  );

  const renderMapCard = useCallback(
    (route: UserCustomRoute) => (
      <MapCard
        key={route.id}
        currentResult={route}
        variant="small"
        showDelete={false}
        onCardMouseEnter={onEnter ? () => onEnter(undefined as unknown as Event) : undefined}
        onCardMouseLeave={onLeave}
        onFavoriteClick={() => onFavoriteClick(route)}
        isFavorite={isFavorite(route.type, route.id)}
        className={styles.mapCard}
        onCardClick={() => onClick?.(route.id, route.slug)}
      />
    ),
    [isFavorite, onFavoriteClick, onEnter, onLeave, onClick]
  );

  const route = routes?.[index];
  if (!route) return null;

  if (isMobile) {
    return (
      <div className={styles.mobilePopup}>
        <Carousel
          uniqueId="user-custom-route-pin-carousel"
          items={routes}
          renderItem={renderMapCard}
          getItemKey={r => `route-${r.id}`}
          initialSlideIndex={index}
          onSlideChange={onIndexChange}
          showPaginationDots={false}
          slidesPerViewMobile={1}
        />
      </div>
    );
  }

  const offset = routes.length > 1 ? clusterPopupOffset : pinPopupOffset;

  return (
    <PaddingAwarePopup
      className={styles.desktopPopup}
      closeButton={false}
      closeOnClick={false}
      focusAfterOpen={false}
      latitude={latitude}
      longitude={longitude}
      maxWidth="none"
      offset={offset}
    >
      <div className={styles.desktopPopupContents}>
        {renderMapCard(route)}
        {routes.length > 1 ? (
          <PopupCarouselControls className={styles.desktopPopupControls} index={index} onIndexChange={onIndexChange} total={routes.length} />
        ) : null}
      </div>
    </PaddingAwarePopup>
  );
};

export default ActiveUserCustomRoutePopup;
